import { css } from '@styled-system/css';
import { center } from '@styled-system/patterns';
import { token } from '@styled-system/tokens';
import type { FC } from 'react';
import { PuffLoader } from 'react-spinners';

import { useAnalysisPageData } from '@/features/analysisPage/hooks/useAnalysisPageData';
import { useAnalysisTabRedirect } from '@/features/analysisPage/hooks/useAnalysisTabRedirect';
import { AnalysisPageNiches } from '@/features/analysisPageNiches/components/AnalysisPageNiches';
import { AnalysisPageSellsHist } from '@/features/analysisPageSellsHist/components/AnalysisPageSellsHist';
import { AnalysisPageStatistics } from '@/features/analysisPageStatistics/components/AnalysisPageStatistics';
import { Message } from '@/shared/components/ui/Message';

import { AnalysisPageHeader } from './AnalysisPageHeader';
import { AnalysisPageTabs } from './AnalysisPageTabs';

interface AnalysisPageProps {
  analysisId: string;
  onBackToMenu?: () => void;
}

const NoContentMessage = ({ message }: { message: string }) => (
  <div className={center({ w: 'full', h: 'full' })}>
    <Message type="error" bordered>
      {message}
    </Message>
  </div>
);

export const AnalysisPage: FC<AnalysisPageProps> = ({ analysisId, onBackToMenu }) => {
  // Hook pour la redirection automatique vers l'onglet par défaut
  useAnalysisTabRedirect(analysisId);

  const { analysisHeaderData, activeTab, handleRename, handleStop, handleDelete, isLoading, error, isUpdating } =
    useAnalysisPageData(analysisId);

  const pageContainerStyles = css({
    w: '100%',
    h: '100vh',
    display: 'flex',
    flexDirection: 'column',
    bg: 'base.bg', // #1a1a1a
    overflow: 'hidden',
  });

  const centerContentStyles = css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    h: '100%',
    p: '2rem',
  });

  if (isLoading) {
    return (
      <div className={pageContainerStyles}>
        <div
          className={center({
            w: 'full',
            h: 'full',
          })}
        >
          <PuffLoader color={token.var('colors.accent.primary')} size={60} />
        </div>
      </div>
    );
  }

  if (error || !analysisHeaderData) {
    return (
      <div className={pageContainerStyles}>
        <div className={centerContentStyles}>
          <Message type="error" bordered>
            {error || 'Analyse non trouvée'}
          </Message>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'statistics':
        return <AnalysisPageStatistics />;
      case 'niches':
        return <AnalysisPageNiches />;
      case 'history':
        return <AnalysisPageSellsHist />;
      default:
        return <NoContentMessage message="Veuillez sélectionner un onglet." />;
    }
  };

  // Si l'analyse est en cours de démarrage, afficher un message d'attente
  if (analysisHeaderData.status === 'starting') {
    return (
      <div className={pageContainerStyles}>
        <AnalysisPageHeader
          analysis={analysisHeaderData}
          handleRename={handleRename}
          handleStop={handleStop}
          handleDelete={handleDelete}
          isUpdating={isUpdating}
          onBackToMenu={onBackToMenu}
        />
        <div
          className={css({
            w: '100%',
            p: '2rem',
          })}
        >
          <Message type="info" bordered>
            Votre analyse est en cours de démarrage et sera bientôt disponible.
          </Message>
        </div>
      </div>
    );
  }

  return (
    <div className={pageContainerStyles}>
      <AnalysisPageHeader
        analysis={analysisHeaderData}
        handleRename={handleRename}
        handleStop={handleStop}
        handleDelete={handleDelete}
        isUpdating={isUpdating}
        onBackToMenu={onBackToMenu}
      />
      <AnalysisPageTabs>{renderTabContent()}</AnalysisPageTabs>
    </div>
  );
};
